/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { useEffect, useRef, useState } from 'react'
import colors from '@sportninja/common/constants/appColors'
import {
  RECORD_BAR_HEIGHT,
  RECORD_BAR_RADIUS,
  buildRecordSegments,
} from '@sportninja/common/features/competition/operations/utils/recordBarSegments'
import type { RecordSegment } from '@sportninja/common/features/competition/operations/utils/recordBarSegments'
import type { InDivisionRecord } from '@sportninja/common/features/competition/operations/types/standings.types'
import { RECORD_COLORS } from './recordColors'

interface RecordBarProps {
  record: InDivisionRecord
  teamName: string
  /** Accessible summary, e.g. "9 W, 2 L, 1 T". */
  label: string
  /**
   * Track width in px, when the CALLER already measured it. The teams sheet
   * renders one bar per row and a division can hold 20-50 of them — each
   * self-measuring bar would mean that many ResizeObservers all firing (and
   * all re-rendering) on a single panel resize. The grid measures its column
   * once and passes the result down instead.
   *
   * Omit it and the bar measures itself, which is what the standings chart
   * does: a handful of bars, each in a track of a different width.
   */
  width?: number
}

/**
 * Rounded-rect path with per-corner control, so a run of segments reads as one
 * bar: only the outer ends are rounded, the interior joins stay square.
 */
const segmentPath = (segment: RecordSegment, height: number): string => {
  const { x, width } = segment
  const r = Math.min(RECORD_BAR_RADIUS, width / 2, height / 2)
  const start = segment.roundStart ? r : 0
  const end = segment.roundEnd ? r : 0

  return [
    `M${x + start},0`,
    `H${x + width - end}`,
    end ? `A${end},${end} 0 0 1 ${x + width},${end}` : '',
    `V${height - end}`,
    end ? `A${end},${end} 0 0 1 ${x + width - end},${height}` : '',
    `H${x + start}`,
    start ? `A${start},${start} 0 0 1 ${x},${height - start}` : '',
    `V${start}`,
    start ? `A${start},${start} 0 0 1 ${x + start},0` : '',
    'Z',
  ]
    .filter(Boolean)
    .join(' ')
}

/**
 * A team's in-division record as a single proportional bar.
 *
 * The SVG is drawn in real pixel units against the measured container width
 * rather than a stretched viewBox: `preserveAspectRatio="none"` would skew the
 * corner radii and render the 2px surface gaps wider than 2px.
 */
const RecordBar = ({
  record,
  teamName,
  label,
  width: measuredWidth,
}: RecordBarProps) => {
  'use memo'

  const containerRef = useRef<HTMLDivElement | null>(null)
  const [ownWidth, setOwnWidth] = useState(0)
  const isMeasuredByCaller = measuredWidth !== undefined

  useEffect(() => {
    // The caller already knows the width — no observer, no state, no re-render
    // on resize. See `width` on the props.
    if (isMeasuredByCaller) {
      return undefined
    }

    const element = containerRef.current
    if (!element) {
      return undefined
    }

    // ResizeObserver rather than a window listener: the panel resizes when the
    // tree rail is collapsed, which fires no window resize event.
    const observer = new ResizeObserver(([entry]) => {
      setOwnWidth(entry.contentRect.width)
    })
    observer.observe(element)

    return () => observer.disconnect()
  }, [isMeasuredByCaller])

  const width = measuredWidth ?? ownWidth
  const segments = buildRecordSegments(record, width)

  return (
    <div ref={containerRef} css={wrapStyle}>
      {segments.length > 0 && (
        <svg
          width={width}
          height={RECORD_BAR_HEIGHT}
          viewBox={`0 0 ${width} ${RECORD_BAR_HEIGHT}`}
          role='img'
          aria-label={`${teamName}: ${label}`}
          css={svgStyle}
        >
          {segments.map((segment) => (
            <path
              key={segment.key}
              d={segmentPath(segment, RECORD_BAR_HEIGHT)}
              fill={RECORD_COLORS[segment.key]}
            >
              <title>{`${teamName} — ${segment.value} ${segment.label}`}</title>
            </path>
          ))}
        </svg>
      )}
    </div>
  )
}

const wrapStyle = css`
  height: ${RECORD_BAR_HEIGHT}px;
  min-width: 0;
`

/* Width/height come from the measured container — never stretch this. */
const svgStyle = css`
  display: block;
  height: ${RECORD_BAR_HEIGHT}px;
`

export const EmptyRecordTrack = ({ teamName }: { teamName: string }) => {
  'use memo'

  return <div css={emptyTrackStyle} role='img' aria-label={teamName} />
}

/* Hatched, not a zeroed bar: the team has played nothing, and a flat 0-width
   bar would read as a real record of all losses.

   Both colours must clear BOTH zebra row colours. The grid alternates
   SECONDARY_300 / SECONDARY_400, so the hatch cannot be either of them —
   it used to be SECONDARY_300, which rendered at 1.00:1 on every even row
   (i.e. invisible) and left only the dashed outline behind. SECONDARY_100 is
   the theme's own border colour and holds 1.77:1 / 2.13:1 against the two
   row surfaces. */
const emptyTrackStyle = css`
  height: ${RECORD_BAR_HEIGHT}px;
  border-radius: ${RECORD_BAR_RADIUS}px;
  border: 1px dashed ${colors.SECONDARY_100};
  background: repeating-linear-gradient(
    45deg,
    transparent,
    transparent 5px,
    ${colors.SECONDARY_100} 5px,
    ${colors.SECONDARY_100} 10px
  );
`

export default RecordBar
